import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, ReferenceLine,
} from 'recharts';
import { RefreshCw, Mail, AlertTriangle, TrendingDown, TrendingUp, ExternalLink, MessageSquare } from 'lucide-react';

import { neonQuery, sendMarketingBrief } from '../services/api';
import { getAccessToken } from '../services/auth';
import BrainChat from '../components/BrainChat';

// How to read paid social. Added 2026-10-07 when Rebecca asked for a "Campaign Brain" to
// say what worked and what did not: the data was already gathered for the 8-hourly recap
// email, so the chat got tools onto it (netlify/functions/ads-ask.js) rather than a
// second brain. This paragraph is the part of the recap's reasoning a model would
// otherwise get wrong: on this account Meta's order COUNT is the unreliable number.
const ADS_GUIDANCE = [
  'You can also read the paid social ads (Meta: Facebook and Instagram). When asked how the ads are doing, what worked, what did not, or whether a promotion paid off, read the data with the ads tools before answering, and give your own verdict with the numbers behind it. Do not send them to Greg for something you can look up.',
  'How to judge an ad here. There are two counts and neither is the truth alone. Meta counts anyone who saw an ad and later bought by any route, so it is a ceiling, and on this account its order count has run several times too high while its revenue is roughly right. Ours counts only people who clicked and arrived carrying the ad tag, so it is a floor. Say both, and say which one you are leaning on.',
  'An offer ad with a promo code has a third test: coupon_orders shows who actually used the code, day by day, how many were first-time customers and how many were existing VIP members. An ad meant to win new customers has not worked if the code was mostly spent by people who already buy from us.',
  'Judge an ad on what it cost per real order and what those orders were worth, against the whole store for the same days (store_sales). Under about $10 of spend an ad has not said anything yet. Lead form ads collect emails, not orders, so zero purchases on one is not a failure by itself.',
  "These tools only read. You cannot pause an ad, change a budget or edit a campaign. If something should be turned off or changed, say so and put it on Greg's list.",
].join(' ');

// The general chat, same one the task boards get. Marketing is the odd brain out:
// it is a dashboard, not a board of tasks, so its work list is marketing_open_items
// and it gets one extra tool for reading its own headline numbers. Growing it a
// mktg_items table instead would put a second marketing to-do list beside the one
// the daily brief already reads.
const CHAT_CONFIG = {
  prefix: 'mb',
  title: 'Marketing Brain',
  sectionLabel: 'Marketing',
  chatGuidance: 'The number everything is judged against is Annual VIP Order Volume, which is active VIPs multiplied by orders per VIP per year. The base erodes in one step every April when the renewal batch runs, and order frequency is steady, so a change in the headline is almost always the base rather than engagement. Read the numbers before quoting any figure, and say which day the snapshot is from.\n' + ADS_GUIDANCE,
  examples: [
    'Which ads are working right now and which are not, and why do you think so?',
    'Did the crab balls code keep bringing in orders after the ads were turned off?',
  ],
  toolLabels: {
    read_the_ads: 'Reading the ad account',
    ads_over_time: 'Reading the ad account',
    orders_we_traced: 'Checking our own orders',
    coupon_orders: 'Checking who used the code',
    store_sales: 'Checking store sales',
  },
};

const NUMBERS_TOOL = [{
  name: 'read_the_numbers',
  description:
    'Read the current marketing headline: Annual VIP Order Volume with its two parts, ' +
    'the same figures against this day last year, and how many open items there are. ' +
    'Use it before quoting any number, so the answer matches what the page shows.',
  input_schema: { type: 'object', properties: {} },
}];

// Paid social, read-only. Each one is run by netlify/functions/ads-ask.js, which returns
// plain text already labelled with what each number can and cannot be trusted for.
const WINDOW_PROPS = {
  since: { type: 'string', description: 'First day, YYYY-MM-DD. Defaults to 14 days back.' },
  until: { type: 'string', description: 'Last day, YYYY-MM-DD. Defaults to today.' },
};
const ADS_TOOLS = [
  {
    name: 'read_the_ads',
    description:
      'The current state of paid social: what Meta says beside what we can trace ourselves ' +
      '(add to cart, checkouts, orders, revenue) for today and for the whole run, per campaign, ' +
      'plus each ad today, what the rules would change, and the recent change log. Start here ' +
      'for any "how are the ads doing" question. Takes about 15 seconds.',
    input_schema: { type: 'object', properties: {} },
  },
  {
    name: 'ads_over_time',
    description:
      "Meta's numbers for any date range, per campaign, ad set or ad, as totals or day by day, " +
      'with whether each one is running or paused now. Use it to compare ads against each other, ' +
      'to look at an ad that is no longer running, or to see when spend started and stopped.',
    input_schema: {
      type: 'object',
      properties: {
        ...WINDOW_PROPS,
        level: { type: 'string', enum: ['campaign', 'adset', 'ad'], description: 'Default campaign.' },
        daily: { type: 'boolean', description: 'true for one row per day. Narrow it with name_contains when you do.' },
        name_contains: { type: 'string', description: 'Only campaigns, ad sets or ads whose name contains this, for example "crab balls".' },
      },
    },
  },
  {
    name: 'orders_we_traced',
    description:
      'Our own record of orders and add-to-carts that arrived carrying an ad tag, listed order ' +
      'by order with the amount, the ad it came from and any promo code used. This is the floor ' +
      "to set beside Meta's ceiling.",
    input_schema: {
      type: 'object',
      properties: {
        ...WINDOW_PROPS,
        name_contains: { type: 'string', description: 'Only tags containing this. Tags are lowercase with hyphens, for example "crab-balls".' },
      },
    },
  },
  {
    name: 'coupon_orders',
    description:
      'Orders that used a promo code, day by day, with how many were first-time customers and ' +
      'how many were active VIP members. Call it with no code to list every code used in the ' +
      'window. Defaults to the last 30 days.',
    input_schema: {
      type: 'object',
      properties: {
        ...WINDOW_PROPS,
        code: { type: 'string', description: 'The code, or part of it. "crabballs" matches FREECRABBALLS and CRABBALLSTEXT.' },
      },
    },
  },
  {
    name: 'store_sales',
    description: 'Orders and revenue for the whole store, day by day, so an ad number has something to be a share of.',
    input_schema: { type: 'object', properties: { ...WINDOW_PROPS } },
  },
];
const ADS_TOOL_NAMES = new Set(ADS_TOOLS.map((t) => t.name));
const CHAT_TOOLS = [...NUMBERS_TOOL, ...ADS_TOOLS];

async function askTheAds(tool, input) {
  const res = await fetch('/.netlify/functions/ads-ask', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAccessToken()}` },
    body: JSON.stringify({ tool, input: input || {} }),
  });
  if (res.status === 401) return 'Your sign-in has run out. Sign in again and ask once more.';
  const d = await res.json().catch(() => null);
  // A timeout comes back as HTML, not JSON. Say so plainly so the answer is "the ad account
  // was slow, try again" and not a guess dressed up as a reading.
  if (!d || !d.text) return 'The ad account did not answer in time. Nothing was read, so do not guess: say it was slow and to ask again.';
  return d.text;
}

// ─────────────────────────────────────────────────────────────────────────────
// Marketing Brain — the single screen for "are we growing, and what do we do
// about it today?"
//
// Everything here reads marketing_daily_metrics, written each morning by
// marketing-snapshot-background.js. Metric definitions live in
// netlify/functions/lib/marketing-metrics.js and nowhere else, so this page,
// the daily brief email and any future surface can never disagree.
//
// THE NORTH STAR (Greg's, and the organizing idea of this page):
//     Annual VIP Order Volume = Active VIPs x Orders per VIP per year
// Both halves must rise. The page leads with the product, then immediately
// breaks it into the two components, because which half is moving determines
// what you should do.
// ─────────────────────────────────────────────────────────────────────────────

const NAVY = 'var(--navy)';
const ORANGE = 'var(--orange)';

const fmtNum = (n, d = 0) =>
  n == null || Number.isNaN(Number(n)) ? '—'
    : Number(n).toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d });
const fmt$ = (n) =>
  n == null || Number.isNaN(Number(n)) ? '—'
    : `$${Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
const pct = (cur, prev) => {
  const c = Number(cur), p = Number(prev);
  if (!isFinite(c) || !isFinite(p) || !p) return null;
  return ((c - p) / p) * 100;
};
const signed = (p) => (p == null ? '—' : `${p >= 0 ? '+' : ''}${p.toFixed(1)}%`);
const trendColor = (p) => (p == null ? '#6b7684' : p > 0.5 ? '#1a7f4b' : p < -0.5 ? '#b3261e' : '#6b7684');

const CHANNEL_LABELS = {
  email: 'Email',
  sms: 'SMS',
  paid_social: 'Paid social',
  meta_organic: 'Meta (organic / click)',
  google: 'Google',
  ai_search: 'AI search (ChatGPT etc.)',
  youtube: 'YouTube',
  unattributed: 'Unattributed',
};
const channelLabel = (k) => CHANNEL_LABELS[k] || k;

const SEGMENT_LABELS = {
  active_90d: 'Active (ordered < 90d)',
  slipping_90_180: 'Slipping (90–180d)',
  cooling_180_365: 'Cooling (180–365d)',
  dormant_365plus: 'Dormant (365d+)',
};
const SEGMENT_ORDER = ['active_90d', 'slipping_90_180', 'cooling_180_365', 'dormant_365plus'];

export default function MarketingBrain() {
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [ns, setNs] = useState(null);
  const [trend, setTrend] = useState([]);
  const [vipBase, setVipBase] = useState([]);
  const [channels, setChannels] = useState([]);
  const [segments, setSegments] = useState([]);
  const [outreach, setOutreach] = useState([]);
  const [chanDays, setChanDays] = useState(30);
  const [briefState, setBriefState] = useState('');
  const [chatOpen, setChatOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const end = new Date();
      const start = new Date(Date.now() - chanDays * 86400000);
      const iso = (d) => d.toISOString().slice(0, 10);

      const [nsRows, trendRows, baseRows, chanRows, segRows, outRows] = await Promise.all([
        neonQuery('mb_north_star'),
        neonQuery('mb_trend', { days: 400 }),
        neonQuery('mb_vip_base_history', { days: 1095 }),
        neonQuery('mb_channels', { start: iso(start), end: iso(end) }),
        neonQuery('mb_segments'),
        neonQuery('mb_outreach_scoreboard'),
      ]);

      setNs(nsRows[0] || null);
      setTrend(trendRows || []);
      setVipBase(baseRows || []);
      setChannels(chanRows || []);
      setSegments(segRows || []);
      setOutreach(outRows || []);
    } catch (e) {
      setErr(e.message || 'Failed to load marketing metrics');
    } finally {
      setLoading(false);
    }
  }, [chanDays]);

  useEffect(() => { load(); }, [load]);

  // Pair each day with the weekday-aligned day 364 back so YoY is apples to
  // apples (a Tuesday against a Tuesday, not against whatever Aug 11 was).
  const nsChart = useMemo(() => {
    const byDate = new Map(trend.map((r) => [String(r.metric_date).slice(0, 10), r]));
    return trend.map((r) => {
      const d = String(r.metric_date).slice(0, 10);
      const prior = new Date(new Date(d).getTime() - 364 * 86400000).toISOString().slice(0, 10);
      const ly = byDate.get(prior);
      return {
        date: d,
        northStar: Number(r.ns_annual_vip_orders) || null,
        lastYear: ly ? Number(ly.ns_annual_vip_orders) : null,
        activeVips: Number(r.active_vips) || null,
        ordersPerVip: Number(r.orders_per_vip) || null,
      };
    });
  }, [trend]);

  const baseChart = useMemo(
    () => vipBase.map((r) => ({ ym: r.ym, activeVips: Number(r.active_vips) })),
    [vipBase]
  );

  // The renewal cliff, measured from the reconstructed monthly base. Every
  // year the base steps down hard in one month (the annual renewal batch).
  // Quantifying it is what turns "we should do retention" into a budget.
  const cliffs = useMemo(() => {
    const out = [];
    for (let i = 1; i < baseChart.length; i++) {
      const prev = baseChart[i - 1].activeVips;
      const cur = baseChart[i].activeVips;
      if (!prev || !cur) continue;
      const drop = cur - prev;
      if (drop < 0 && Math.abs(drop / prev) > 0.08) {
        out.push({ ym: baseChart[i].ym, from: prev, to: cur, drop, pct: (drop / prev) * 100 });
      }
    }
    return out;
  }, [baseChart]);

  const segMatrix = useMemo(() => {
    const m = { vip: {}, non_vip: {} };
    for (const r of segments) {
      if (!m[r.status]) m[r.status] = {};
      m[r.status][r.segment] = r;
    }
    return m;
  }, [segments]);

  const totalChanOrders = channels.reduce((s, c) => s + Number(c.orders || 0), 0);
  const attributedOrders = channels
    .filter((c) => c.channel !== 'unattributed')
    .reduce((s, c) => s + Number(c.orders || 0), 0);
  const coverage = totalChanOrders ? (attributedOrders / totalChanOrders) * 100 : 0;

  const sendBrief = async (dryRun) => {
    setBriefState(dryRun ? 'Building preview…' : 'Sending…');
    try {
      const res = await sendMarketingBrief({ dryRun });
      if (dryRun && typeof res === 'string') {
        const w = window.open('', '_blank');
        if (w) { w.document.write(res); w.document.close(); }
        setBriefState('Preview opened in a new tab.');
      } else {
        setBriefState('Brief sent.');
      }
    } catch (e) {
      setBriefState(`Failed: ${e.message}`);
    }
  };

  // Headline runs on the RENEWING cohort (Greg, 2026-08-13): members who have
  // opted out are already leaving, so including them flatters the base. Fall
  // back to the all-active columns for rows written before those existed.
  const renewingVips     = ns ? (ns.renewing_vips ?? ns.active_vips) : null;
  const notRenewingVips  = ns ? (ns.not_renewing_vips ?? null) : null;
  const ordersPerRenewing = ns ? (ns.orders_per_renewing_vip ?? ns.orders_per_vip) : null;
  const nsValue          = ns ? (ns.ns_renewing_order_volume ?? ns.ns_annual_vip_orders) : null;
  const lastYearNs       = ns ? (ns.ly_ns_renewing ?? ns.ly_ns) : null;
  const lastYearRenewing = ns ? (ns.ly_renewing_vips ?? ns.ly_active_vips) : null;
  const lastYearFreq     = ns ? (ns.ly_orders_per_renewing ?? ns.ly_orders_per_vip) : null;

  const nsYoY  = ns ? pct(ns.ns_annual_vip_orders, ns.ly_ns) : null;
  const vipYoY = ns ? pct(ns.active_vips, ns.ly_active_vips) : null;
  const freqYoY = ns ? pct(ns.orders_per_vip, ns.ly_orders_per_vip) : null;

  if (loading && !ns) {
    return <div className="page"><div className="card"><div className="card-body">Loading marketing metrics…</div></div></div>;
  }

  const noData = !ns;

  // Hands the chat the same headline the page draws, rather than letting it work
  // one out of raw rows and quietly disagree with the screen.
  const readTheNumbers = async (name, input) => {
    if (ADS_TOOL_NAMES.has(name)) return askTheAds(name, input);
    if (name !== 'read_the_numbers') return null;
    const d = await neonQuery('mb_numbers', {});
    if (!d || !d.latest) return 'No metrics snapshot has been written yet.';
    const l = d.latest;
    const day = String(l.metric_date || "").slice(0, 10);
    return [
      `Snapshot ${day}`,
      `Annual VIP Order Volume: ${l.ns_annual_vip_orders} (last year ${l.ly_ns})`,
      `Active VIPs: ${l.active_vips} (last year ${l.ly_active_vips})`,
      `Orders per VIP per year: ${l.orders_per_vip} (last year ${l.ly_orders_per_vip})`,
      `Renewing VIPs: ${l.renewing_vips}, their order volume ${l.ns_renewing_order_volume}`,
      `Open marketing items: ${d.openItems}`,
    ].join('\n');
  };
  return (
    <div className="page">
      <div className="breadcrumb">
        <a href="#marketing">Marketing</a><span> / </span><span>Marketing Brain</span>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, color: NAVY }}>Marketing Brain</h1>
          <div style={{ color: '#6b7684', fontSize: 14, marginTop: 4 }}>
            Everything we do, measured against one number.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {briefState && <span style={{ fontSize: 13, color: '#6b7684' }}>{briefState}</span>}
          <button className="btn btn-outline" onClick={() => setChatOpen(true)}>
            <MessageSquare size={15} style={{ marginRight: 6 }} />Ask the brain
          </button>
          <button className="btn btn-outline" onClick={() => sendBrief(true)}>
            <Mail size={15} style={{ marginRight: 6 }} />Preview brief
          </button>
          <button className="btn btn-primary" onClick={load}>
            <RefreshCw size={15} style={{ marginRight: 6 }} />Refresh
          </button>
        </div>
      </div>

      {chatOpen && (
        <BrainChat
          config={CHAT_CONFIG}
          extraTools={CHAT_TOOLS}
          runExtraTool={readTheNumbers}
          onChanged={load}
          onClose={() => setChatOpen(false)}
        />
      )}
      {err && (
        <div className="card" style={{ marginBottom: 16, borderLeft: `4px solid ${ORANGE}` }}>
          <div className="card-body" style={{ color: '#b3261e' }}>{err}</div>
        </div>
      )}

      {noData && !err && (
        <div className="card" style={{ marginBottom: 16, borderLeft: `4px solid ${ORANGE}` }}>
          <div className="card-body">
            <strong>No snapshots yet.</strong> The nightly job writes one row per day into
            <code> marketing_daily_metrics</code>. Run a backfill to populate history:
            <pre style={{ marginTop: 8, fontSize: 12 }}>
POST /.netlify/functions/marketing-snapshot-background {'{'}"days": 800{'}'}
            </pre>
          </div>
        </div>
      )}

      {/* ── North Star ─────────────────────────────────────────────────── */}
      {ns && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-header">
            <span>North Star &middot; Annual VIP Order Volume</span>
            <span style={{ fontSize: 12, color: '#6b7684', fontWeight: 400 }}>
              as of {String(ns.metric_date).slice(0, 10)}
            </span>
          </div>
          <div className="card-body">
            <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div>
                <div style={{ fontSize: 46, fontWeight: 700, color: NAVY, lineHeight: 1 }}>
                  {fmtNum(ns.ns_annual_vip_orders)}
                </div>
                <div style={{ fontSize: 14, color: trendColor(nsYoY), fontWeight: 600, marginTop: 6 }}>
                  {nsYoY != null && (nsYoY >= 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />)}{' '}
                  {signed(nsYoY)} vs last year ({fmtNum(ns.ly_ns)})
                </div>
                <div style={{ fontSize: 12, color: '#8a94a0', marginTop: 4 }}>
                  Every order from every active member.
                </div>
              </div>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <Component label="Active VIPs" value={fmtNum(ns.active_vips)} delta={vipYoY} sub="the base" />
                <div style={{ alignSelf: 'center', fontSize: 26, color: '#c3ccd6', fontWeight: 300 }}>×</div>
                <Component label="Orders per VIP / yr" value={fmtNum(ns.orders_per_vip, 2)} delta={freqYoY} sub="the frequency" />
              </div>
            </div>

            {notRenewingVips > 0 && (
              <div style={{ marginTop: 14, padding: '12px 14px', background: '#fff6ea', borderLeft: `3px solid ${ORANGE}`, borderRadius: 4, fontSize: 14, color: '#3d4753' }}>
                <strong style={{ color: ORANGE, fontSize: 16 }}>{fmtNum(notRenewingVips)}</strong>
                {' '}of the {fmtNum(ns.active_vips)} active members are <strong>set NOT to renew</strong>, against{' '}
                <strong>{fmtNum(renewingVips)}</strong> renewing. They are still customers today, so turning them
                back is a conversation, not an acquisition budget — call centre or an email segment.
              </div>
            )}

            {/* The diagnosis, stated plainly. Which half is moving decides the work. */}
            {vipYoY != null && freqYoY != null && (
              <div style={{ marginTop: 16, padding: '12px 14px', background: 'var(--peach)', borderRadius: 4, fontSize: 14, color: '#3d4753' }}>
                {Math.abs(vipYoY) > Math.abs(freqYoY) ? (
                  <>
                    <strong>The base is the constraint, not engagement.</strong> Orders per VIP is {signed(freqYoY)} YoY
                    while the VIP count is {signed(vipYoY)}. Frequency work can't offset a shrinking base —
                    acquisition and renewal retention are where the North Star is won.
                  </>
                ) : (
                  <>
                    <strong>Frequency is the constraint.</strong> The VIP count is {signed(vipYoY)} YoY while orders
                    per VIP is {signed(freqYoY)}. Getting existing members to order one more time a year is the
                    cheapest lever available.
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── North Star trend ───────────────────────────────────────────── */}
      {nsChart.length > 1 && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-header">North Star over time (vs prior year)</div>
          <div className="card-body" style={{ height: 280 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={nsChart} margin={{ top: 8, right: 16, bottom: 4, left: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} minTickGap={40} />
                <YAxis tick={{ fontSize: 11 }} width={56} />
                <Tooltip formatter={(v) => fmtNum(v)} />
                <Line type="monotone" dataKey="lastYear" name="Last year" stroke="#c3ccd6" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="northStar" name="North Star" stroke="#00395C" strokeWidth={2.5} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* ── The renewal cliff ──────────────────────────────────────────── */}
      {baseChart.length > 1 && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-header">
            <span>Active VIP base &middot; the annual renewal cliff</span>
          </div>
          <div className="card-body">
            <div style={{ height: 260 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={baseChart} margin={{ top: 8, right: 16, bottom: 4, left: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" />
                  <XAxis dataKey="ym" tick={{ fontSize: 10 }} minTickGap={20} />
                  <YAxis tick={{ fontSize: 11 }} width={56} domain={['dataMin - 500', 'dataMax + 300']} />
                  <Tooltip formatter={(v) => fmtNum(v)} />
                  <Bar dataKey="activeVips" name="Active VIPs" fill="#00395C" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            {cliffs.length > 0 && (
              <div style={{ marginTop: 12, fontSize: 14, color: '#3d4753' }}>
                <div style={{ fontWeight: 700, color: NAVY, marginBottom: 6 }}>
                  Membership lost at each renewal batch
                </div>
                <table className="product-table" style={{ maxWidth: 560 }}>
                  <thead>
                    <tr><th>Month</th><th style={{ textAlign: 'right' }}>Before</th><th style={{ textAlign: 'right' }}>After</th><th style={{ textAlign: 'right' }}>Lost</th></tr>
                  </thead>
                  <tbody>
                    {cliffs.map((c) => (
                      <tr key={c.ym}>
                        <td>{c.ym}</td>
                        <td style={{ textAlign: 'right' }}>{fmtNum(c.from)}</td>
                        <td style={{ textAlign: 'right' }}>{fmtNum(c.to)}</td>
                        <td style={{ textAlign: 'right', color: '#b3261e', fontWeight: 700 }}>
                          {fmtNum(c.drop)} ({c.pct.toFixed(1)}%)
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div style={{ marginTop: 10, color: '#6b7684' }}>
                  The base grows steadily all year on new signups, then drops in one step when the annual
                  renewal batch runs. Every point of retention saved here is worth more than a point of
                  frequency, because it compounds into the following year's base.
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Channels ───────────────────────────────────────────────────── */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header">
          <span>Where orders come from</span>
          <span>
            {[7, 30, 90].map((d) => (
              <button
                key={d}
                className={`btn ${chanDays === d ? 'btn-primary' : 'btn-ghost'}`}
                style={{ marginLeft: 6, padding: '4px 10px', fontSize: 12 }}
                onClick={() => setChanDays(d)}
              >{d}d</button>
            ))}
          </span>
        </div>
        <div className="card-body">
          {coverage < 80 && totalChanOrders > 0 && (
            <div style={{ display: 'flex', gap: 8, padding: '10px 12px', background: '#fff6ea', borderLeft: `3px solid ${ORANGE}`, marginBottom: 12, fontSize: 13, color: '#3d4753' }}>
              <AlertTriangle size={16} style={{ color: ORANGE, flexShrink: 0 }} />
              <div>
                Only <strong>{coverage.toFixed(0)}%</strong> of orders carry a recorded source. Every channel
                number below is a <strong>floor, not a total</strong> — closing this gap is the highest-value
                instrumentation work on the roadmap.
              </div>
            </div>
          )}
          <table className="product-table">
            <thead>
              <tr>
                <th>Channel</th>
                <th style={{ textAlign: 'right' }}>Orders</th>
                <th style={{ textAlign: 'right' }}>Revenue</th>
                <th style={{ textAlign: 'right' }}>Share of attributed</th>
              </tr>
            </thead>
            <tbody>
              {channels.map((c) => {
                const isUnattr = c.channel === 'unattributed';
                return (
                  <tr key={c.channel} style={isUnattr ? { color: '#8a94a0', fontStyle: 'italic' } : undefined}>
                    <td>{channelLabel(c.channel)}</td>
                    <td style={{ textAlign: 'right' }}>{fmtNum(c.orders)}</td>
                    <td style={{ textAlign: 'right' }}>{fmt$(c.revenue)}</td>
                    <td style={{ textAlign: 'right' }}>
                      {isUnattr ? '—' : `${attributedOrders ? ((Number(c.orders) / attributedOrders) * 100).toFixed(1) : '0.0'}%`}
                    </td>
                  </tr>
                );
              })}
              {!channels.length && <tr><td colSpan={4} style={{ color: '#8a94a0' }}>No data in this window.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Segments ───────────────────────────────────────────────────── */}
      {segments.length > 0 && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-header">Segments &middot; who to talk to, and about what</div>
          <div className="card-body">
            <table className="product-table">
              <thead>
                <tr>
                  <th>Segment</th>
                  <th style={{ textAlign: 'right' }}>VIP members</th>
                  <th style={{ textAlign: 'right' }}>Non-VIP customers</th>
                  <th>The play</th>
                </tr>
              </thead>
              <tbody>
                {SEGMENT_ORDER.map((seg) => {
                  const v = segMatrix.vip[seg];
                  const n = segMatrix.non_vip[seg];
                  return (
                    <tr key={seg}>
                      <td>{SEGMENT_LABELS[seg]}</td>
                      <td style={{ textAlign: 'right', fontWeight: 600 }}>{fmtNum(v && v.customers)}</td>
                      <td style={{ textAlign: 'right' }}>{fmtNum(n && n.customers)}</td>
                      <td style={{ fontSize: 13, color: '#6b7684' }}>{SEGMENT_PLAYS[seg]}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── CS outreach ────────────────────────────────────────────────── */}
      {outreach.length > 0 && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-header">VIP conversion outreach (customer service)</div>
          <div className="card-body">
            <table className="product-table">
              <thead>
                <tr>
                  <th>Rep</th>
                  <th style={{ textAlign: 'right' }}>Sent</th>
                  <th style={{ textAlign: 'right' }}>Joined VIP after</th>
                  <th style={{ textAlign: 'right' }}>Rate</th>
                  <th style={{ textAlign: 'right' }}>Avg would-have-saved</th>
                </tr>
              </thead>
              <tbody>
                {outreach.map((r) => (
                  <tr key={r.rep}>
                    <td>{r.rep}</td>
                    <td style={{ textAlign: 'right' }}>{fmtNum(r.sent)}</td>
                    <td style={{ textAlign: 'right' }}>{fmtNum(r.converted)}</td>
                    <td style={{ textAlign: 'right', fontWeight: 600 }}>
                      {r.sent ? `${((r.converted / r.sent) * 100).toFixed(1)}%` : '—'}
                    </td>
                    <td style={{ textAlign: 'right' }}>{fmt$(r.avg_would_have_saved)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ marginTop: 10, fontSize: 13, color: '#6b7684' }}>
              &ldquo;Joined VIP after&rdquo; counts a membership purchased on or after the send date. It is
              directional, not causal — the .eml hand-off has no open or click tracking, so a 0% rate cannot
              currently distinguish a weak offer from a measurement blind spot.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const SEGMENT_PLAYS = {
  active_90d: 'Protect. Cross-sell new SKUs; ask for the review.',
  slipping_90_180: 'Nudge. A reason to reorder now beats a discount later.',
  cooling_180_365: 'Win back before renewal — this is next April\'s cliff forming.',
  dormant_365plus: 'Reactivate or let go. Cheapest test: one strong seasonal offer.',
};

function Component({ label, value, delta, sub }) {
  return (
    <div style={{ padding: '12px 16px', background: '#f4f7fa', borderRadius: 4, minWidth: 150 }}>
      <div style={{ fontSize: 11, color: '#6b7684', textTransform: 'uppercase', letterSpacing: '.06em' }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 700, color: NAVY, lineHeight: 1.2 }}>{value}</div>
      <div style={{ fontSize: 12, color: trendColor(delta) }}>{signed(delta)} YoY</div>
      {sub && <div style={{ fontSize: 11, color: '#8a94a0', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}
